/**
 * Admin Panel Component
 * Controls for stream, DJ, lighting, and sound
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Slider } from '../ui/slider';
import { X, Crown, Zap, Sparkles, Music2 } from 'lucide-react';

// Lighting FX Modes with descriptions
const LIGHTING_FX_MODES = [
  { id: 'chill', name: 'Chill', icon: '🌙', desc: 'Relaxed vibes' },
  { id: 'auto', name: 'Audio Sync', icon: '🎵', desc: 'Reacts to music' },
  { id: 'sweep', name: 'Sweep', icon: '🌊', desc: 'Side to side' },
  { id: 'wave', name: 'Wave', icon: '〰️', desc: 'Smooth waves' },
  { id: 'pulse', name: 'Pulse', icon: '💓', desc: 'Soft pulse' },
  { id: 'spotlight', name: 'Spotlight', icon: '🔦', desc: 'Center focus' },
];

// Preset color palettes
const COLOR_PRESETS = [
  { name: 'Rave', primary: '#9333EA', secondary: '#DC2626' },
  { name: 'Cyber', primary: '#06B6D4', secondary: '#22D3EE' },
  { name: 'Fire', primary: '#F97316', secondary: '#DC2626' },
  { name: 'Void', primary: '#7C3AED', secondary: '#1E1B4B' },
  { name: 'Neon', primary: '#22C55E', secondary: '#FACC15' },
  { name: 'Ice', primary: '#3B82F6', secondary: '#E0F2FE' },
];

const prettyRoom = (room) => {
  const r = room || 'main';
  if (r === 'main') return 'Club Floor';
  if (r === 'city') return 'City Block';
  if (r === 'sewer') return 'The Sewers';
  if (r === 'apartments') return 'Apartments';
  if (r === 'house_900_diner') return 'Turtle Shack';
  if (r.startsWith('house_')) return 'Inside a House';
  if (r.startsWith('apt_')) return 'Inside an Apartment';
  return r.replace(/_/g, ' ');
};

const AdminPanel = ({
  isOpen,
  onClose,
  streamUrl,
  setStreamUrl,
  currentVideoId,
  isStreaming,
  onStreamControl,
  currentDJ,
  setCurrentDJ,
  djPlaying,
  setDjPlaying,
  lightingMode,
  onLightingChange,
  lightingFx = {},
  onLightingFxChange,
  soundEnabled,
  setSoundEnabled,
  npcDJs = [],
  socketRef,
  players = {},
  onSummon,
  onSummonAll,
  onGoTo
}) => {
  const [showAdvancedLighting, setShowAdvancedLighting] = useState(false);
  
  if (!isOpen) return null;

  const handleFxModeChange = (mode) => {
    if (socketRef?.current?.connected) {
      socketRef.current.emit('admin_lighting_fx', { fx_mode: mode });
    }
    onLightingFxChange?.({ ...lightingFx, fx_mode: mode });
  };

  const handleColorPreset = (preset) => {
    if (socketRef?.current?.connected) {
      socketRef.current.emit('admin_lighting_fx', {
        primary_color: preset.primary,
        secondary_color: preset.secondary
      });
    }
    onLightingFxChange?.({ ...lightingFx, primary_color: preset.primary, secondary_color: preset.secondary });
  };

  const handleSliderChange = (key, value) => {
    if (socketRef?.current?.connected) {
      socketRef.current.emit('admin_lighting_fx', { [key]: value });
    }
    onLightingFxChange?.({ ...lightingFx, [key]: value });
  };

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/90"
      style={{ touchAction: 'auto' }}
    >
      {/* Close button - Always visible */}
      <Button 
        onClick={onClose}
        className="fixed top-4 right-4 z-[110] bg-red-600 hover:bg-red-500 rounded-full w-12 h-12"
      >
        <X className="w-6 h-6" />
      </Button>
      
      {/* Scrollable content */}
      <div className="h-full overflow-y-auto p-4 pt-20 pb-8">
        <div className="max-w-md mx-auto space-y-4">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Crown className="w-6 h-6 text-red-500" />
            Admin Controls
          </h2>
          
          {/* Players Online + Teleport */}
          <div className="bg-gray-800/80 rounded-xl p-4 space-y-3" data-testid="admin-players-online">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">🌐 Players Online ({Object.keys(players).length + 1}/100)</h3>
              {Object.keys(players).length > 0 && (
                <Button
                  onClick={onSummonAll}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-xs h-8"
                  data-testid="summon-all-btn"
                >
                  ⚡ Summon ALL
                </Button>
              )}
            </div>
            {Object.keys(players).length === 0 ? (
              <p className="text-white/40 text-sm">No other players online right now</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {Object.entries(players).map(([sid, p]) => (
                  <div key={sid} className="flex items-center justify-between bg-black/40 rounded-lg px-3 py-2" data-testid={`online-player-${p.name}`}>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{p.name || 'Guest'}</p>
                      <p className="text-white/40 text-[10px] capitalize truncate">{prettyRoom(p.room)}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <Button
                        onClick={() => onSummon?.(sid)}
                        className="bg-purple-700 hover:bg-purple-600 text-[11px] h-7 px-2.5"
                        data-testid={`summon-player-btn-${p.name}`}
                      >
                        📥 Summon
                      </Button>
                      <Button
                        onClick={() => onGoTo?.(sid)}
                        variant="outline"
                        className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/20 text-[11px] h-7 px-2.5"
                        data-testid={`goto-player-btn-${p.name}`}
                      >
                        🚀 Go To
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Stream Control */}
          <div className="bg-gray-800/80 rounded-xl p-4 space-y-3">
            <h3 className="text-lg font-semibold text-white">YouTube Stream</h3>
            <Input
              value={streamUrl}
              onChange={(e) => setStreamUrl(e.target.value)}
              placeholder="Paste YouTube URL..."
              className="bg-black/50 border-white/20 text-white text-lg py-3"
            />
            {streamUrl && (
              <p className={`text-sm ${currentVideoId ? 'text-green-400' : 'text-red-400'}`}>
                {currentVideoId ? `✓ Video ID: ${currentVideoId}` : '✗ Invalid URL'}
              </p>
            )}
            <div className="flex gap-2">
              <Button 
                onClick={() => onStreamControl(true)}
                className="flex-1 bg-green-600 hover:bg-green-500 py-3"
                disabled={!streamUrl || !currentVideoId}
              >
                ▶ Start
              </Button>
              <Button 
                onClick={() => onStreamControl(false)}
                className="flex-1 bg-red-600 hover:bg-red-500 py-3"
              >
                ⏹ Stop
              </Button>
            </div>
            <p className={`text-center text-sm ${isStreaming ? 'text-green-400' : 'text-white/40'}`}>
              {isStreaming ? '🔴 LIVE' : '⚫ OFFLINE'}
            </p>
          </div>
          
          {/* DJ Control */}
          <div className="bg-gray-800/80 rounded-xl p-4 space-y-3">
            <h3 className="text-lg font-semibold text-white">DJ Selection</h3>
            <div className="grid grid-cols-2 gap-2">
              {npcDJs.map(dj => (
                <button
                  key={dj.id}
                  onClick={() => setCurrentDJ(dj)}
                  className={`p-3 rounded-lg border-2 text-left ${
                    currentDJ?.id === dj.id 
                      ? 'border-red-500 bg-red-500/20' 
                      : 'border-white/20 hover:border-red-500/50'
                  }`}
                >
                  <div className="font-medium text-white">{dj.name}</div>
                  <div className="text-xs text-white/60">{dj.style}</div>
                </button>
              ))}
            </div>
            <Button 
              onClick={() => setDjPlaying(!djPlaying)}
              className={`w-full py-3 ${djPlaying ? 'bg-red-600' : 'bg-green-600'}`}
            >
              {djPlaying ? '🛑 Hide DJ' : '🎧 Show DJ'}
            </Button>
          </div>
          
          {/* Lighting Control - Basic */}
          <div className="bg-gray-800/80 rounded-xl p-4 space-y-3">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              Lighting Mode
            </h3>
            <div className="flex gap-2">
              {['ambient', 'rave', 'drop'].map(mode => (
                <Button
                  key={mode}
                  onClick={() => onLightingChange(mode)}
                  className={`flex-1 capitalize ${
                    lightingMode === mode ? 'bg-purple-600' : 'bg-gray-700'
                  }`}
                >
                  {mode}
                </Button>
              ))}
            </div>
            
            {/* Toggle Advanced Lighting */}
            <Button
              onClick={() => setShowAdvancedLighting(!showAdvancedLighting)}
              className="w-full mt-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {showAdvancedLighting ? 'Hide' : 'Show'} Dynamic Lighting FX
            </Button>
          </div>
          
          {/* Advanced Lighting FX Panel */}
          {showAdvancedLighting && (
            <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 rounded-xl p-4 space-y-4 border border-purple-500/30">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Music2 className="w-5 h-5 text-pink-400" />
                Dynamic Lighting FX
              </h3>
              
              {/* FX Mode Selection */}
              <div className="space-y-2">
                <label className="text-sm text-white/70">Effect Mode</label>
                <div className="grid grid-cols-3 gap-2">
                  {LIGHTING_FX_MODES.map(mode => (
                    <button
                      key={mode.id}
                      onClick={() => handleFxModeChange(mode.id)}
                      className={`p-2 rounded-lg border-2 text-center transition-all ${
                        lightingFx.fx_mode === mode.id
                          ? 'border-pink-500 bg-pink-500/30 shadow-lg shadow-pink-500/20'
                          : 'border-white/10 bg-black/20 hover:border-pink-500/50'
                      }`}
                    >
                      <div className="text-xl">{mode.icon}</div>
                      <div className="text-xs text-white font-medium">{mode.name}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Color Presets */}
              <div className="space-y-2">
                <label className="text-sm text-white/70">Color Presets</label>
                <div className="grid grid-cols-3 gap-2">
                  {COLOR_PRESETS.map(preset => (
                    <button
                      key={preset.name}
                      onClick={() => handleColorPreset(preset)}
                      className="p-2 rounded-lg border border-white/10 hover:border-white/30 transition-all"
                      style={{
                        background: `linear-gradient(135deg, ${preset.primary} 0%, ${preset.secondary} 100%)`
                      }}
                    >
                      <span className="text-xs font-bold text-white drop-shadow-lg">{preset.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Speed Slider */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className="text-sm text-white/70">Speed</label>
                  <span className="text-sm text-pink-400">{(lightingFx.speed || 1).toFixed(1)}x</span>
                </div>
                <Slider
                  value={[lightingFx.speed || 1]}
                  onValueChange={([v]) => handleSliderChange('speed', v)}
                  min={0.1}
                  max={3}
                  step={0.1}
                  className="[&>span]:bg-pink-500"
                />
              </div>
              
              {/* Intensity Slider */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <label className="text-sm text-white/70">Intensity</label>
                  <span className="text-sm text-pink-400">{Math.round((lightingFx.intensity || 0.7) * 100)}%</span>
                </div>
                <Slider
                  value={[lightingFx.intensity || 0.7]}
                  onValueChange={([v]) => handleSliderChange('intensity', v)}
                  min={0}
                  max={1}
                  step={0.05}
                  className="[&>span]:bg-pink-500"
                />
              </div>
              
              {/* Audio Reactivity - only show when in auto mode */}
              {lightingFx.fx_mode === 'auto' && (
                <>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-sm text-white/70">🔊 Bass Reactivity</label>
                      <span className="text-sm text-orange-400">{(lightingFx.bass_reactivity || 1).toFixed(1)}x</span>
                    </div>
                    <Slider
                      value={[lightingFx.bass_reactivity || 1]}
                      onValueChange={([v]) => handleSliderChange('bass_reactivity', v)}
                      min={0}
                      max={2}
                      step={0.1}
                      className="[&>span]:bg-orange-500"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <label className="text-sm text-white/70">🎶 Treble Reactivity</label>
                      <span className="text-sm text-cyan-400">{(lightingFx.treble_reactivity || 0.5).toFixed(1)}x</span>
                    </div>
                    <Slider
                      value={[lightingFx.treble_reactivity || 0.5]}
                      onValueChange={([v]) => handleSliderChange('treble_reactivity', v)}
                      min={0}
                      max={2}
                      step={0.1}
                      className="[&>span]:bg-cyan-500"
                    />
                  </div>
                  
                  <div className="text-xs text-white/50 text-center p-2 bg-black/30 rounded-lg">
                    💡 Auto mode syncs lights with YouTube audio. Unmute video for best effect!
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Sound Control */}
          <div className="bg-gray-800/80 rounded-xl p-4 space-y-3">
            <h3 className="text-lg font-semibold text-white">Sound Effects</h3>
            <Button 
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`w-full py-3 ${soundEnabled ? 'bg-green-600' : 'bg-gray-700'}`}
            >
              {soundEnabled ? '🔊 SFX On' : '🔇 SFX Off'}
            </Button>
          </div>
          
          {/* Close button at bottom */}
          <Button 
            onClick={onClose}
            className="w-full py-4 bg-gray-700 hover:bg-gray-600"
          >
            Close Admin Panel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
